import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, TimeoutError, timeout } from 'rxjs';
import { AppRoutes } from 'src/app/app-routes';
import { ErrorModalComponent } from 'src/app/components/error-modal/error-modal.component';
import { SubmittableForm } from 'src/app/models/base/submittable-form.base';
import { RegisterStep } from 'src/app/models/interfaces/register-stage.model';
import { withFirstFrom } from 'src/app/models/utils/observable-utils';
import { DeviceStoreService } from 'src/app/services/device-store/device-store.service';
import { RegisterService } from 'src/app/services/register-service/register.service';

enum RegisterErrors {
  REGISTER_FAIL = 10,
  NEW_VERIFY_CODE_FAIL,
  DUPLICATE_DRIVER,
  PHONE_MISSING,
  PHONE_INVALID,
  PASSWORD_MISSING = 18
}

@Component({
  selector: 'app-phone-enter',
  templateUrl: './phone-enter.component.html',
  styleUrls: ['./phone-enter.component.scss']
})
export class PhoneEnterComponent extends SubmittableForm implements OnInit {
  
  faBack = faArrowLeft;
  AppRoutes = AppRoutes;
  private isLoggedIn: boolean | undefined;

  private isLoggedInSubscription: Subscription | undefined;

  constructor(
    private router: Router,
    translateService: TranslateService, 
    private deviceStore: DeviceStoreService,
    private registerService: RegisterService,
    private spinner: NgxSpinnerService,
    private modalService: NgbModal)
  {
    super(translateService)
  }

  override form = new FormGroup({
    phone: new FormControl('', {
      validators: [Validators.required]
    }),
    password: new FormControl('', {
      validators: [Validators.required, Validators.minLength(6)]
    }),
  });

  ngOnInit(): void 
  {
    this.isLoggedInSubscription = this.deviceStore.getIsSignedOn().subscribe(isLoggedIn =>
      {
        this.isLoggedIn = isLoggedIn;
        if (isLoggedIn)
        {
          this.form.controls.password.disable();
        }
        else
        {
          this.form.controls.password.enable();
        }
      });
  }

  onPhoneEntered()
  {
    this.spinner.show();

    const isLoggedIn = this.isLoggedIn;
    return super.onConfirm(
      () => {
          if (isLoggedIn)
          {
            return this.registerService.sendDetails({ mobileTelNo: (this.form.value.phone as string) }).pipe(timeout({ first: 60000 }));
          }
          return this.registerService.register((this.form.value.phone as string), (this.form.value.password as string)).pipe(timeout({ first: 60000 }));
      },
      {
        onSuccess: () =>
        {
          this.spinner.hide();
          
          withFirstFrom(this.deviceStore.user).then(user =>
          {
              if (!isLoggedIn && user)
              {
                this.deviceStore.setRegisterStage({
                  userId: user.id,
                  step: RegisterStep.PHONE_CONFIRM,
                });
              }
          }).then(() => this.router.navigate([AppRoutes.PhoneConfirm], { queryParamsHandling: 'preserve' }));
        },
        onError: (error) => {
          this.spinner.hide();
          
          error.subscribe((err: TimeoutError | { code: number }) => {
            if (err instanceof TimeoutError)
            {
              const modalRef = this.modalService.open(ErrorModalComponent, { backdrop: 'static', centered: true });

              modalRef.componentInstance.errText = this.translateService.instant("IDS_ERR_SERVER_TIMEOUT");
            }
            else
            {
              let errMsg = '';

              switch(err.code)
              {
                case RegisterErrors.REGISTER_FAIL:
                  errMsg = this.translateService.instant('IDS_REGISTER_ERROR_MSG', {errorType: this.translateService.instant('IDS_REGISTER_ERROR_GENERIC_FAIL')});
                  break;
                case RegisterErrors.NEW_VERIFY_CODE_FAIL:
                  errMsg = this.translateService.instant('IDS_REGISTER_ERROR_MSG', {errorType: this.translateService.instant('IDS_REGISTER_ERROR_NEW_CODE')});
                  break;
                case RegisterErrors.DUPLICATE_DRIVER:
                  errMsg = this.translateService.instant('IDS_REGISTER_ERROR_MSG', {errorType: this.translateService.instant('IDS_REGISTER_ERROR_DUPLICATE_DRIVER')});
                  break;
                case RegisterErrors.PHONE_MISSING:
                  errMsg = this.translateService.instant('IDS_REGISTER_ERROR_MSG', {errorType: this.translateService.instant('IDS_REGISTER_ERROR_PHONE_MISSING')});
                  break;
                case RegisterErrors.PHONE_INVALID:
                  errMsg = this.translateService.instant('IDS_REGISTER_ERROR_MSG', {errorType: this.translateService.instant('IDS_REGISTER_ERROR_PHONE_INVALID')});
                  break;
                case RegisterErrors.PASSWORD_MISSING:
                  errMsg = this.translateService.instant('IDS_REGISTER_ERROR_MSG', {errorType: this.translateService.instant('IDS_REGISTER_ERROR_PASSWORD_MISSING')});
                  break;
              }

              const modalRef = this.modalService.open(ErrorModalComponent, {backdrop: 'static', centered: true });

              modalRef.componentInstance.errText = errMsg;
            }
          })
        }
      }
    );
  }
}
